import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dt, inr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

const STATUS = { pending: "bg-warning/15 text-warning", approved: "bg-success/15 text-success", rejected: "bg-destructive/15 text-destructive" };

export default function WalletRequests() {
  const { user } = useAuth();
  const canReview = ["super_admin", "admin"].includes(user.role);
  const canCreate = ["merchant", "distributor"].includes(user.role);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", type: "credit", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/wallet-requests").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/wallet-requests", { amount: parseFloat(form.amount), type: form.type, note: form.note });
      toast.success("Wallet request submitted");
      setOpen(false); setForm({ amount: "", type: "credit", note: "" });
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const review = async (id, action) => {
    try {
      await api.patch(`/wallet-requests/${id}`, { action });
      toast.success(`Request ${action}d`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{canReview ? "Review wallet requests" : "Wallet requests"}</h2>
          <p className="text-sm text-muted-foreground">Manual wallet credits and debits, approved by operations.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-wallet-request-button"><Plus className="mr-1.5 h-4 w-4" /> Add wallet request</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New wallet request</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5"><Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="wallet-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit">Credit (add funds)</SelectItem>
                      <SelectItem value="debit">Debit (deduct funds)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Amount (₹)</Label><Input type="number" min="1" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="wallet-amount-input" /></div>
                <div className="space-y-1.5"><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional" data-testid="wallet-note-input" /></div>
                <DialogFooter><Button type="submit" disabled={saving} data-testid="wallet-submit-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                {canReview && <TableHead>Account</TableHead>}
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Status</TableHead>
                {canReview && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {rows && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No requests yet.</TableCell></TableRow>}
              {rows && rows.map((r) => (
                <TableRow key={r.id} data-testid={`wallet-request-row-${r.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(r.created_at)}</TableCell>
                  {canReview && <TableCell className="text-sm">{r.business_name || r.user_name}</TableCell>}
                  <TableCell><Badge variant="outline" className={r.type === "credit" ? "text-success" : "text-destructive"}>{r.type}</Badge></TableCell>
                  <TableCell className="text-right font-mono text-sm">{inr(r.amount)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{r.note || "—"}</TableCell>
                  <TableCell><Badge className={STATUS[r.status]}>{r.status}</Badge>{r.reviewed_by_name && <div className="text-[11px] text-muted-foreground mt-0.5">by {r.reviewed_by_name}</div>}</TableCell>
                  {canReview && (
                    <TableCell className="text-right">
                      {r.status === "pending" ? (
                        <div className="flex justify-end gap-1.5">
                          <Button size="icon" variant="outline" className="h-8 w-8 text-success" onClick={() => review(r.id, "approve")} data-testid={`approve-wallet-${r.id}`}><Check className="h-4 w-4" /></Button>
                          <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => review(r.id, "reject")} data-testid={`reject-wallet-${r.id}`}><X className="h-4 w-4" /></Button>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
